import React, { useState } from 'react';
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_LIGHT,
  ACCENT_BORDER,
  TEXT_DARK,
  FONT,
} from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, GhostCard, ScreenWrap } from '../components/ui/Primitives';
import { useSchedule } from '../lib/useSchedule';
import { useBreakpoint } from '../lib/useBreakpoint';

// Pull the same palette table the Schedule tab uses so the Home countdown
// matches the kind-specific color of whatever's currently in progress.
const KIND_PALETTE = {
  session: { accent: ACCENT, accentDark: ACCENT_DARK },
  flex: { accent: ACCENT, accentDark: ACCENT_DARK },
  joint: { accent: '#0d9488', accentDark: '#0f766e' },
};

// 0=before Wed, 1=Wed (arrival), 2=Thu (Day 1 of 2), 3=Fri (Day 2 of 2), 4=after.
function eventPhase(now) {
  const wed = new Date('2026-05-20T00:00:00-07:00').getTime();
  const thu = new Date('2026-05-21T00:00:00-07:00').getTime();
  const fri = new Date('2026-05-22T00:00:00-07:00').getTime();
  const sat = new Date('2026-05-23T00:00:00-07:00').getTime();
  if (now < wed) return 0;
  if (now < thu) return 1;
  if (now < fri) return 2;
  if (now < sat) return 3;
  return 4;
}

function dayBadge(phase) {
  switch (phase) {
    case 0: return null; // pre-event: don't show a status line
    case 1: return 'Arrival Day';
    case 2: return 'Day 1 of 2';
    case 3: return 'Day 2 of 2';
    default: return 'Event Complete';
  }
}

export default function HomeScreen({ isPostEvent }) {
  const { current, next, secondsRemaining, totalSeconds } = useSchedule();
  const phase = eventPhase(Date.now());
  const bp = useBreakpoint();

  // ── Desktop: two-column layout ─────────────────────────────────────────
  if (bp === 'desktop') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!isPostEvent ? (
            <CurrentSessionCard
              session={current}
              next={next}
              secondsRemaining={secondsRemaining}
              totalSeconds={totalSeconds}
              phase={phase}
            />
          ) : (
            <EventWrappedCard />
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <EventInfoCard phase={phase} />
          {!isPostEvent && next && <NextUpCard session={next} />}
          <AiTeaserCard session={current || next} />
        </div>
      </div>
    );
  }

  // ── Mobile / tablet: existing single-column ────────────────────────────
  return (
    <ScreenWrap>
      <EventInfoCard phase={phase} />

      {!isPostEvent ? (
        <CurrentSessionCard
          session={current}
          next={next}
          secondsRemaining={secondsRemaining}
          totalSeconds={totalSeconds}
          phase={phase}
        />
      ) : (
        <EventWrappedCard />
      )}

      {!isPostEvent && next && <NextUpCard session={next} />}

      <AiTeaserCard session={current || next} />
    </ScreenWrap>
  );
}

function CurrentSessionCard({ session, next, secondsRemaining, totalSeconds, phase }) {
  // Nothing is in progress — pre-event, arrival day, or between sessions.
  if (!session) {
    const headline =
      phase === 0 ? 'Event begins May 20'
      : phase === 1 ? 'Arrival Day'
      : next ? 'Up Next'
      : 'No sessions in progress';

    const sub =
      phase === 0 ? 'May 20–22 · SCUHS · Whittier, CA'
      : phase === 1 ? 'Travel & hotel check-in · informal gathering tonight'
      : next ? `${next.title} · ${next.time}`
      : 'See you tomorrow';

    return (
      <GhostCard
        style={{
          background: `linear-gradient(135deg, ${ACCENT}18, ${ACCENT_DARK}10)`,
          border: `1px solid ${ACCENT_BORDER}`,
          padding: '18px 20px',
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.55)',
            letterSpacing: '0.1em',
            marginBottom: 6,
          }}
        >
          {headline.toUpperCase()}
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
          {next?.title || (phase === 1 ? 'Welcome to Whittier' : 'ELX Forum')}
        </div>
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)' }}>{sub}</div>
      </GhostCard>
    );
  }

  const palette = KIND_PALETTE[session.kind || 'session'] || KIND_PALETTE.session;
  const pct = Math.min(100, ((totalSeconds - secondsRemaining) / totalSeconds) * 100);
  const countdown = formatCountdown(secondsRemaining);
  const [startTime, endTime] = (session.time || '').split('–').map((p) => p.trim());

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accentDark} 100%)`,
        borderRadius: 16,
        padding: '18px 20px 16px',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.05,
          backgroundImage:
            'repeating-linear-gradient(0deg,transparent,transparent 19px,rgba(255,255,255,0.8) 20px),repeating-linear-gradient(90deg,transparent,transparent 19px,rgba(255,255,255,0.8) 20px)',
        }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 9,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{
                background: 'rgba(255,255,255,0.18)',
                borderRadius: 6,
                padding: '2px 8px',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '0.08em',
                }}
              >
                {session.num.toUpperCase()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 0 5px rgba(255,255,255,0.6)',
                }}
              />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                Now
              </span>
            </div>
          </div>
          <span
            style={{
              fontSize: 10.5,
              color: 'rgba(255,255,255,0.55)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Ic.Clock size={11} color="rgba(255,255,255,0.55)" /> {session.time}
          </span>
        </div>
        <h2
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            marginBottom: 13,
          }}
        >
          {session.title}
        </h2>

        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 9 }}>
            <span
              style={{
                fontSize: countdown.length > 8 ? 26 : 34,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#fff',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {countdown}
            </span>
            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.5)' }}>remaining</span>
          </div>
          <div
            style={{
              height: 3,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: pct + '%',
                background: 'rgba(255,255,255,0.65)',
                borderRadius: 2,
                transition: 'width 1s linear',
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
            <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.38)' }}>
              Started {startTime}
            </span>
            <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.38)' }}>
              Ends {endTime}
            </span>
          </div>

          {session.prompt && (
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <CopyPromptButton prompt={session.prompt} variant="light" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NextUpCard({ session }) {
  const [startTime] = (session.time || '').split('–').map((p) => p.trim());
  return (
    <Card style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: ACCENT_LIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 10.5, fontWeight: 700, color: ACCENT }}>
          {abbreviate(session.num)}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#94a3b8',
            letterSpacing: '0.07em',
            marginBottom: 2,
          }}
        >
          NEXT UP
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_DARK, lineHeight: 1.3 }}>
          {session.title}
        </div>
        <div
          style={{
            fontSize: 11,
            color: '#94a3b8',
            marginTop: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Ic.Clock size={10} color="#94a3b8" /> Starts {startTime} · {session.duration}
        </div>
      </div>
      {session.prompt ? (
        <CopyPromptButton prompt={session.prompt} variant="dark" />
      ) : (
        <Ic.ChevronRight size={15} />
      )}
    </Card>
  );
}

function CopyPromptButton({ prompt, variant = 'dark' }) {
  const [copied, setCopied] = useState(false);
  if (!prompt) return null;

  const onClick = (e) => {
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(prompt).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const palette =
    variant === 'light'
      ? copied
        ? { bg: 'rgba(255,255,255,0.95)', color: '#16a34a', border: '1px solid rgba(255,255,255,0.95)' }
        : { bg: 'rgba(255,255,255,0.16)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)' }
      : copied
        ? { bg: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }
        : { bg: ACCENT_LIGHT, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` };

  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        background: palette.bg,
        border: palette.border,
        color: palette.color,
        borderRadius: 7,
        padding: '6px 11px',
        fontSize: 11.5,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: FONT,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        transition: 'all 0.15s',
      }}
    >
      {copied ? <Ic.Check size={12} color={palette.color} /> : <Ic.Copy size={12} color={palette.color} />}
      {copied ? 'Copied!' : 'Copy Prompt'}
    </button>
  );
}

function EventInfoCard({ phase }) {
  return (
    <GhostCard
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        marginTop: 6,
        marginBottom: 6,
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: '-0.005em',
            marginBottom: 4,
          }}
        >
          ELX - Executive Leadership Forum
        </div>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: 'rgba(255,255,255,0.6)',
            marginBottom: 6,
          }}
        >
          SCUHS · Whittier, CA
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)' }}>Hosted by</span>
          <img
            src="/chirocongress-logo-white.png"
            alt="ChiroCongress"
            style={{ height: 13, objectFit: 'contain', opacity: 0.45 }}
          />
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
          May 20–22
        </div>
        {dayBadge(phase) && (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
            {dayBadge(phase)}
          </div>
        )}
      </div>
    </GhostCard>
  );
}

function AiTeaserCard({ session }) {
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${ACCENT_LIGHT}, rgba(15,74,143,0.06))`,
        border: `1px solid ${ACCENT_BORDER}`,
        borderRadius: 13,
        padding: '12px 15px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Ic.Spark size={14} color="#fff" />
      </div>
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
          AI Workshop Prompt Ready
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
          Tap "Ask ELX AI" to explore {session ? session.title : 'today’s sessions'}
        </div>
      </div>
    </div>
  );
}

function EventWrappedCard() {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(148,163,184,0.18), rgba(100,116,139,0.1))',
        border: '1px solid rgba(148,163,184,0.2)',
        borderRadius: 16,
        padding: '18px 20px',
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: '#94a3b8',
          letterSpacing: '0.1em',
          marginBottom: 5,
        }}
      >
        EVENT WRAPPED
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
        ELX Forum Complete
      </div>
      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
        May 20–22 · Whittier, CA
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          style={{
            background: ACCENT,
            border: 'none',
            borderRadius: 8,
            padding: '8px 14px',
            color: '#fff',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: FONT,
          }}
        >
          View Summaries
        </button>
        <button
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 8,
            padding: '8px 14px',
            color: 'rgba(255,255,255,0.65)',
            fontSize: 12.5,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: FONT,
          }}
        >
          Commitments
        </button>
      </div>
    </div>
  );
}

function abbreviate(num) {
  if (!num) return '';
  if (num === 'Closing') return 'CL';
  if (num === 'Joint Session') return 'JS';
  if (num === 'Open Discussion') return 'OD';
  if (num === 'Countdown') return 'CD';
  if (num === 'Welcome') return 'WL';
  if (num === 'Campus Tour') return 'CT';
  return num.replace('Session ', 'S');
}

// Pretty-print remaining seconds: MM:SS for short sessions, HH:MM:SS once it
// crosses an hour, "Xd HH:MM:SS" for multi-day windows (like the pre-event
// test session).
function formatCountdown(total) {
  total = Math.max(0, Math.floor(total));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}
